// Hooks do domínio de LEADS (CRM) e do catálogo de status do lead. Mesmo
// padrão de use-brands.ts / use-calls.ts:
//   - `src/api/leads.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam espalhadas pelas telas, string por
// string — centralizar não é renomear. O React Query invalida por PREFIXO, então
// uma chave que divergisse uma letra viraria dado velho na tela sem erro nenhum.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  assignLead,
  createLead,
  createLeadStatusDef,
  deleteLead,
  deleteLeadStatusDef,
  getLead,
  getLeadByPhone,
  getLeadDados,
  getLeads,
  getLeadStatuses,
  reprocessLeadStatuses,
  resetLeadStatusDefs,
  updateLeadStatus,
  updateLeadStatusDef,
  type LeadDetail,
  type LeadListItem,
  type LeadStatusDef,
  type LeadStatusInput,
  type NewLeadInput,
} from "@/api/leads";
import { dialerKeys } from "./use-dialer.js";

// As chaves DESTE domínio. Quatro ressalvas herdadas que valem leitura:
//
//  - `all` (["leads"]) é PREFIXO de `list` e de `queuePriorityPreview`:
//    invalidar ["leads"] derruba as três, e é assim que as telas contam com
//    isso hoje (toda mutação de lead invalida só ["leads"]).
//  - `detail` (["lead", id]) fica DE FORA desse prefixo — é outra raiz. Nenhuma
//    mutação a invalida hoje; a ficha se atualiza pelo próprio refetchInterval.
//    Divergência pré-existente, preservada.
//  - `byPhone` usa ["manual-dial-lead", telefone], sem parentesco com nenhuma
//    das outras, embora chame o mesmo domínio (GET /v1/leads/by-phone). Também
//    pré-existente: é o preview do softphone, que ninguém invalida.
//  - `statuses` (["lead-statuses"]) é PREFIXO de `allStatuses`
//    (["lead-statuses","all"]) — a tela de catálogo edita a lista COM inativos
//    e invalida só o prefixo, contando com isso para derrubar as duas.
export const leadKeys = {
  // O valor exato que `lead-row-actions.tsx` exportava como LEADS_QUERY_KEY.
  all: ["leads"] as const,
  list: (filters: { status: string; search: string }) => ["leads", filters] as const,
  queuePriorityPreview: (limit: number) => ["leads", "queue-priority-preview", limit] as const,
  detail: (leadId: string | null | undefined) => ["lead", leadId] as const,
  dados: (leadId: string) => ["lead-dados", leadId] as const,
  byPhone: (phone: string) => ["manual-dial-lead", phone] as const,
  statuses: ["lead-statuses"] as const,
  allStatuses: ["lead-statuses", "all"] as const,
};

// `journey` não é deste domínio e por isso segue literal aqui: é o passo
// "Cadastre o primeiro lead" da jornada, que muda quando um lead nasce. Quando
// esse domínio sair, ela vai junto.
const JOURNEY_QUERY_KEY = ["onboarding", "journey"] as const;
// A fila do discador também não é daqui, mas já tem dono: `dialerKeys.leads`
// (hooks/use-dialer.ts) guarda o MESMO valor, ["dialer-leads"], que estava
// literal nesta linha até o domínio do discador sair do barrel.
const DIALER_LEADS_QUERY_KEY = dialerKeys.leads;

// --- Leitura ---

// Tabela de /management/leads. `placeholderData` mantém a página anterior na
// tela enquanto a nova chega — sem ela a tabela pisca vazia a cada digitada da
// busca. O recorte vai inteiro na chave: filtro diferente é OUTRA lista.
export function useLeads(filters: { status: string; search: string }) {
  return useQuery({
    queryKey: leadKeys.list(filters),
    queryFn: () =>
      getLeads({
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.search ? { q: filters.search } : {}),
      }),
    placeholderData: (prev) => prev,
  });
}

// Amostra usada só para PREVER a ordem da fila na tela de status do lead — daí
// a chave própria: é o mesmo endpoint da tabela, com outro propósito e outro
// recorte, e dividir a entrada de cache faria um sobrescrever o outro.
export function useLeadsQueuePreview(limit: number, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: leadKeys.queuePriorityPreview(limit),
    queryFn: () => getLeads({ limit, offset: 0 }),
    enabled: options.enabled,
  });
}

// Um lead pelo id. Três telas dividem esta entrada de cache de propósito (a
// gaveta do kanban, o deep-link do HubSpot no detalhe da ligação e a ficha do
// lead), então quem já abriu uma reaproveita nas outras.
export function useLead(
  leadId: string | null | undefined,
  options: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    queryKey: leadKeys.detail(leadId),
    queryFn: () => getLead(leadId as string),
    enabled: options.enabled,
    staleTime: options.staleTime,
  });
}

// A ficha do lead: MESMA chave do hook acima, mas a linha da tabela entra como
// `initialData` (abre sem flicker) e o poll roda enquanto o diálogo está
// aberto — se o HubSpot mudar a etapa, a timeline acompanha sem fechar e abrir.
// Hook à parte, e não um parâmetro do `useLead`, porque `initialData` muda o
// tipo do retorno: aqui `data` nunca é undefined.
const LEAD_SHEET_POLL_MS = 15_000;

export function useLeadSheet(lead: LeadListItem, open: boolean) {
  return useQuery({
    queryKey: leadKeys.detail(lead.id),
    queryFn: () => getLead(lead.id),
    enabled: open,
    initialData: { ...lead, progress: [] } as LeadDetail,
    refetchInterval: open ? LEAD_SHEET_POLL_MS : false,
    refetchOnWindowFocus: "always",
  });
}

// Preview da associação que o back fará na discagem manual (GET
// /v1/leads/by-phone). `retry: false`: número que não casa com lead nenhum é
// resposta legítima, insistir devolveria o mesmo.
export function useLeadByPhone(phone: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: leadKeys.byPhone(phone),
    queryFn: () => getLeadByPhone(phone),
    enabled: options.enabled,
    retry: false,
  });
}

// Dados ricos do CRM no card do discador. `staleTime` alto e sem retry: o dado
// não muda no meio da ligação, e a fila não pode ficar refém do HubSpot.
const LEAD_DADOS_STALE_MS = 5 * 60_000;

export function useLeadDados(leadId: string) {
  return useQuery({
    queryKey: leadKeys.dados(leadId),
    queryFn: () => getLeadDados(leadId),
    staleTime: LEAD_DADOS_STALE_MS,
    retry: false,
  });
}

// Catálogo ATIVO da org, compartilhado via cache entre telas e badges.
// Ordenado pelo back (sort_order).
export function useLeadStatuses(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: leadKeys.statuses,
    queryFn: () => getLeadStatuses(),
    enabled: options.enabled,
  });
}

// Catálogo COM os inativos — só a tela que edita o catálogo precisa deles (um
// status inativo guarda a posição que terá de volta quando for reativado).
export function useAllLeadStatuses() {
  return useQuery({
    queryKey: leadKeys.allStatuses,
    queryFn: () => getLeadStatuses(true),
  });
}

// --- Escrita nos leads ---

// Mudar status, reatribuir e apagar derrubam a MESMA coisa (a lista inteira,
// por prefixo), então a invalidação é do hook. Os `handlers` entram como
// parâmetro, e não via `mutate(vars, { onSuccess })`, para a ORDEM original ser
// preservada (tela primeiro — toast, fechar —, invalidação depois).
//
// Os `mutationFn` chamam a função de api dentro de uma seta, em vez de passá-la
// direta: o TanStack invoca o mutationFn com (variáveis, contexto), e uma
// função de api que aceite um segundo parâmetro receberia o contexto da mutação
// como se fosse o dado dela.
function useInvalidateLeads() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: leadKeys.all });
}

export function useUpdateLeadStatus(
  leadId: string,
  handlers: { onSuccess?: (lead: LeadListItem) => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: (status: string) => updateLeadStatus(leadId, status),
    onSuccess: (updated) => {
      handlers.onSuccess?.(updated);
      invalidate();
    },
    onError: handlers.onError,
  });
}

export function useAssignLead(
  leadId: string,
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: (assignedTo: string | null) => assignLead(leadId, assignedTo),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

export function useDeleteLead(
  leadId: string,
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateLeads();
  return useMutation({
    mutationFn: () => deleteLead(leadId),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

// Cadastro manual. As DUAS invalidações importam: leads (a tabela) e a jornada
// (o passo "Cadastre o primeiro lead" e o contador da sidebar). O erro chega
// tipado como `ApiError` para a tela ramificar pelo 409 de telefone duplicado.
export function useCreateLead(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: NewLeadInput) => createLead(input),
    onSuccess: () => {
      handlers.onSuccess?.();
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
      void queryClient.invalidateQueries({ queryKey: JOURNEY_QUERY_KEY });
    },
    onError: handlers.onError,
  });
}

// A importação de CSV não tem hook de mutação: ela quebra o arquivo em lotes e
// remapeia o número da linha de cada erro para a linha ORIGINAL do CSV — isso é
// da tela, não do domínio. O que ela reaproveita daqui é a invalidação, que
// precisa acontecer inclusive no ERRO (uma falha no 3º lote deixa os dois
// primeiros gravados).
export function useInvalidateAfterLeadImport() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: leadKeys.all });
    void queryClient.invalidateQueries({ queryKey: JOURNEY_QUERY_KEY });
  };
}

// --- Escrita no catálogo de status ---

function useInvalidateLeadStatuses() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: leadKeys.statuses });
}

// Criar e editar são a MESMA mutação do ponto de vista do cache, mas NÃO do
// corpo: a `key` é imutável no back, então ela só existe no create. Daí a união
// discriminada por `id` em vez de um objeto com campos opcionais — assim não
// existe a forma "editar mandando key", que o back recusaria.
export type SaveLeadStatusDefVars =
  | { id: null; input: LeadStatusInput }
  | { id: string; patch: Partial<Omit<LeadStatusInput, "key">> };

export function useSaveLeadStatusDef(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateLeadStatuses();
  return useMutation({
    mutationFn: (vars: SaveLeadStatusDefVars) =>
      vars.id === null ? createLeadStatusDef(vars.input) : updateLeadStatusDef(vars.id, vars.patch),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

// Patch rápido da linha (definir padrão, ativar/desativar) sem abrir o diálogo.
export function usePatchLeadStatusDef(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateLeadStatuses();
  return useMutation({
    mutationFn: (args: { id: string; patch: { isDefault?: boolean; active?: boolean } }) =>
      updateLeadStatusDef(args.id, args.patch),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

export function useDeleteLeadStatusDef(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateLeadStatuses();
  return useMutation({
    mutationFn: (id: string) => deleteLeadStatusDef(id),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

// Reordenar é OTIMISTA na tela (a lista já se move e volta atrás no erro), por
// isso `previous`/`next` viajam nas variáveis: quem desfaz é o handler da tela,
// que é dona do estado local. Os slots vêm prontos — reindexar de 0..n aqui
// reescreveria também o sortOrder dos inativos.
export interface ReorderLeadStatusArgs {
  previous: LeadStatusDef[];
  next: LeadStatusDef[];
  patches: { id: string; sortOrder: number }[];
}

export function useReorderLeadStatusDefs(
  handlers: {
    onSuccess?: (args: ReorderLeadStatusArgs) => void;
    onError?: (error: Error, args: ReorderLeadStatusArgs) => void;
  } = {},
) {
  const invalidate = useInvalidateLeadStatuses();
  return useMutation({
    mutationFn: ({ patches }: ReorderLeadStatusArgs) =>
      Promise.all(patches.map(({ id, sortOrder }) => updateLeadStatusDef(id, { sortOrder }))),
    onSuccess: (_result, args) => handlers.onSuccess?.(args),
    onError: (error: Error, args) => handlers.onError?.(error, args),
    onSettled: invalidate,
  });
}

// Reprocessar re-deriva o status dos leads pela etapa do CRM, e restaurar troca
// o catálogo inteiro: as duas mudam LEAD, não só o catálogo — daí a fila do
// discador entrar na invalidação (ver DIALER_LEADS_QUERY_KEY).
export function useReprocessLeadStatuses(
  handlers: {
    onSuccess?: (result: { reprocessed: number; scanned: number }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => reprocessLeadStatuses(),
    onSuccess: (result) => {
      handlers.onSuccess?.(result);
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
      void queryClient.invalidateQueries({ queryKey: DIALER_LEADS_QUERY_KEY });
    },
    onError: handlers.onError,
  });
}

export function useResetLeadStatusDefs(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => resetLeadStatusDefs(),
    onSuccess: () => {
      handlers.onSuccess?.();
      void queryClient.invalidateQueries({ queryKey: leadKeys.statuses });
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
      void queryClient.invalidateQueries({ queryKey: DIALER_LEADS_QUERY_KEY });
    },
    onError: handlers.onError,
  });
}
